import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { cva } from 'class-variance-authority';
import { forwardRef, useId, useState, useRef, useEffect, useMemo } from 'react';
import { jsxs, jsx } from 'react/jsx-runtime';
import { ChevronDown, Check, Search, X } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';

// src/lib/cn.ts
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
var buttonVariants = cva(
  // base — transform + shadow + colour transitions for a premium, tactile feel.
  // Lift on hover, gentle press on active. Reduced-motion users get instant states
  // (global CSS zeroes transition-duration).
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded font-medium transition-[transform,box-shadow,background-color,border-color,color] duration-base ease-out-expo outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:translate-y-0 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-[1.1em] [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-foreground shadow-sm hover:bg-primary/95 hover:-translate-y-0.5 hover:shadow-primary",
        secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/95 hover:-translate-y-0.5 hover:shadow-md",
        outline: "border border-border bg-transparent text-foreground hover:bg-muted hover:border-foreground/25",
        ghost: "bg-transparent hover:bg-muted text-foreground",
        link: "bg-transparent text-primary underline-offset-4 hover:underline p-0 h-auto active:scale-100",
        danger: "bg-danger text-danger-foreground shadow-sm hover:bg-danger/95 hover:-translate-y-0.5 hover:shadow-md"
      },
      size: {
        sm: "h-9 px-3 text-sm",
        md: "h-11 px-5 text-sm",
        lg: "h-12 px-7 text-base",
        icon: "h-11 w-11"
      }
    },
    defaultVariants: { variant: "primary", size: "md" }
  }
);
var Button = forwardRef(function Button2({ className, variant, size, loading = false, disabled, children, ...props }, ref) {
  return /* @__PURE__ */ jsxs(
    "button",
    {
      ref,
      className: cn(buttonVariants({ variant, size }), className),
      disabled: disabled || loading,
      "aria-busy": loading || void 0,
      ...props,
      children: [
        loading && /* @__PURE__ */ jsx(
          "span",
          {
            className: "size-[1em] animate-spin rounded-full border-2 border-current border-t-transparent",
            "aria-hidden": "true"
          }
        ),
        children
      ]
    }
  );
});
var badgeVariants = cva(
  "inline-flex items-center gap-1 rounded px-2.5 py-0.5 text-xs font-medium leading-none",
  {
    variants: {
      variant: {
        neutral: "bg-muted text-muted-foreground",
        primary: "bg-primary/10 text-primary",
        secondary: "bg-secondary/15 text-secondary-foreground",
        success: "bg-success/12 text-success",
        warning: "bg-warning/15 text-warning",
        danger: "bg-danger/12 text-danger",
        outline: "border border-border text-foreground"
      }
    },
    defaultVariants: { variant: "neutral" }
  }
);
function Badge({ className, variant, ...props }) {
  return /* @__PURE__ */ jsx("span", { className: cn(badgeVariants({ variant }), className), ...props });
}
var Container = forwardRef(
  function Container2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn("mx-auto w-full max-w-[1440px] px-4 sm:px-6", className),
        ...props
      }
    );
  }
);
var SECTION_PAD = {
  sm: "py-8 sm:py-10",
  md: "py-10 sm:py-14",
  lg: "py-16 sm:py-20",
  xl: "py-20 sm:py-28 lg:py-32"
};
var Section = forwardRef(function Section2({ className, spacing = "md", ...props }, ref) {
  return /* @__PURE__ */ jsx("section", { ref, className: cn(SECTION_PAD[spacing], className), ...props });
});
var Card = forwardRef(function Card2({ className, interactive = false, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn(
        "rounded-lg border border-border bg-card text-card-foreground shadow-sm",
        interactive && "transition-[transform,box-shadow,border-color] duration-base ease-out-expo hover:-translate-y-1 hover:border-primary/20 hover:shadow-lg",
        className
      ),
      ...props
    }
  );
});
function Skeleton({ className, ...props }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "relative overflow-hidden rounded bg-muted",
        "after:absolute after:inset-0 after:-translate-x-full after:animate-[shimmer_1.6s_infinite]",
        "after:bg-gradient-to-r after:from-transparent after:via-background/40 after:to-transparent",
        className
      ),
      "aria-hidden": "true",
      ...props
    }
  );
}
var fieldBase = "flex h-11 w-full rounded border border-input bg-background px-3.5 text-sm text-foreground shadow-sm transition-[color,box-shadow,border-color] duration-fast ease-out-expo placeholder:text-muted-foreground hover:border-foreground/20 focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-danger aria-[invalid=true]:ring-danger/40";
var Input = forwardRef(function Input2({ id, className, label, error, hint, leading, required, ...props }, ref) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : void 0;
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
    label && /* @__PURE__ */ jsxs("label", { htmlFor: inputId, className: "text-sm font-medium text-foreground", children: [
      label,
      required && /* @__PURE__ */ jsx("span", { className: "ml-0.5 text-danger", "aria-hidden": "true", children: "*" })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      leading && /* @__PURE__ */ jsx("span", { className: "pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground", children: leading }),
      /* @__PURE__ */ jsx(
        "input",
        {
          ref,
          id: inputId,
          className: cn(fieldBase, leading && "pl-10", className),
          "aria-invalid": error ? true : void 0,
          "aria-describedby": describedBy,
          "aria-required": required || void 0,
          required,
          ...props
        }
      )
    ] }),
    error ? /* @__PURE__ */ jsx("p", { id: `${inputId}-error`, className: "text-xs text-danger", role: "alert", children: error }) : hint ? /* @__PURE__ */ jsx("p", { id: `${inputId}-hint`, className: "text-xs text-muted-foreground", children: hint }) : null
  ] });
});
var Select = forwardRef(function Select2({ id, className, label, error, hint, options, placeholder, required, ...props }, ref) {
  const autoId = useId();
  const selectId = id ?? autoId;
  const describedBy = error ? `${selectId}-error` : hint ? `${selectId}-hint` : void 0;
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
    label && /* @__PURE__ */ jsxs("label", { htmlFor: selectId, className: "text-sm font-medium text-foreground", children: [
      label,
      required && /* @__PURE__ */ jsx("span", { className: "ml-0.5 text-danger", "aria-hidden": "true", children: "*" })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsxs(
        "select",
        {
          ref,
          id: selectId,
          className: cn(
            "flex h-11 w-full appearance-none rounded border border-input bg-background pl-3.5 pr-10 text-sm text-foreground shadow-sm transition-colors duration-fast ease-out-expo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-danger",
            className
          ),
          "aria-invalid": error ? true : void 0,
          "aria-describedby": describedBy,
          required,
          ...props,
          children: [
            placeholder && /* @__PURE__ */ jsx("option", { value: "", disabled: true, children: placeholder }),
            options.map((opt) => /* @__PURE__ */ jsx("option", { value: opt.value, children: opt.label }, opt.value))
          ]
        }
      ),
      /* @__PURE__ */ jsx(
        ChevronDown,
        {
          className: "pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground",
          "aria-hidden": "true"
        }
      )
    ] }),
    error ? /* @__PURE__ */ jsx("p", { id: `${selectId}-error`, className: "text-xs text-danger", role: "alert", children: error }) : hint ? /* @__PURE__ */ jsx("p", { id: `${selectId}-hint`, className: "text-xs text-muted-foreground", children: hint }) : null
  ] });
});
function SearchableSelect({
  label,
  placeholder = "All",
  options,
  value,
  onChange,
  className,
  "aria-label": ariaLabel,
  placement = "auto"
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [dropUp, setDropUp] = useState(false);
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const labelId = useId();
  useEffect(() => {
    if (!open) return;
    if (placement === "top") {
      setDropUp(true);
    } else if (placement === "bottom") {
      setDropUp(false);
    } else if (rootRef.current) {
      const rect = rootRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setDropUp(spaceBelow < 340);
    }
  }, [open, placement]);
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    function onKeyDown(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);
  useEffect(() => {
    if (open) searchRef.current?.focus();
    else setQuery("");
  }, [open]);
  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, query]);
  const selectedLabel = options.find((o) => o.value === value)?.label;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-1.5", className), ref: rootRef, children: [
    label && /* @__PURE__ */ jsx("span", { id: labelId, className: "text-sm font-medium text-foreground", children: label }),
    /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          "aria-haspopup": "listbox",
          "aria-expanded": open,
          "aria-labelledby": label ? labelId : void 0,
          "aria-label": label ? void 0 : ariaLabel,
          onClick: () => setOpen((o) => !o),
          className: "flex h-11 w-full items-center justify-between rounded border border-input bg-background pl-3.5 pr-3 text-sm text-foreground shadow-sm transition-colors duration-fast ease-out-expo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
          children: [
            /* @__PURE__ */ jsx("span", { className: cn("truncate", !selectedLabel && "text-muted-foreground"), children: selectedLabel ?? placeholder }),
            /* @__PURE__ */ jsx(
              ChevronDown,
              {
                className: cn(
                  "size-4 shrink-0 text-muted-foreground transition-transform duration-fast ease-out-expo",
                  open && "rotate-180"
                ),
                "aria-hidden": "true"
              }
            )
          ]
        }
      ),
      open && /* @__PURE__ */ jsxs(
        "div",
        {
          role: "listbox",
          className: cn(
            "absolute z-30 w-full rounded border border-border bg-card p-2 shadow-lg animate-in fade-in-0 zoom-in-95 duration-fast ease-out-expo",
            dropUp ? "bottom-full mb-1.5" : "top-full mt-1.5"
          ),
          children: [
            /* @__PURE__ */ jsxs("div", { className: "relative mb-2", children: [
              /* @__PURE__ */ jsx(
                Search,
                {
                  className: "pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground",
                  "aria-hidden": "true"
                }
              ),
              /* @__PURE__ */ jsx(
                "input",
                {
                  ref: searchRef,
                  type: "text",
                  value: query,
                  onChange: (e) => setQuery(e.target.value),
                  placeholder: "Search...",
                  "aria-label": `Search ${ariaLabel ?? label ?? "options"}`,
                  className: "h-9 w-full rounded border border-input bg-background pl-8 pr-3 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
                }
              )
            ] }),
            /* @__PURE__ */ jsx("div", { className: "max-h-56 overflow-y-auto", children: filteredOptions.length === 0 ? /* @__PURE__ */ jsx("p", { className: "px-2 py-1.5 text-sm text-muted-foreground", children: options.length === 0 ? "No options" : "No matches" }) : /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-0.5", children: filteredOptions.map((opt) => {
              const active = opt.value === value;
              return /* @__PURE__ */ jsxs(
                "button",
                {
                  type: "button",
                  role: "option",
                  "aria-selected": active,
                  onClick: () => {
                    onChange(opt.value);
                    setOpen(false);
                  },
                  className: cn(
                    "flex items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted",
                    active ? "text-primary" : "text-foreground"
                  ),
                  children: [
                    /* @__PURE__ */ jsx("span", { className: "truncate", children: opt.label }),
                    active && /* @__PURE__ */ jsx(Check, { className: "size-4 shrink-0", "aria-hidden": "true" })
                  ]
                },
                opt.value
              );
            }) }) })
          ]
        }
      )
    ] })
  ] });
}
var Checkbox = forwardRef(function Checkbox2({ id, className, label, hint, ...props }, ref) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return /* @__PURE__ */ jsxs(
    "label",
    {
      htmlFor: inputId,
      className: cn(
        "group flex cursor-pointer select-none items-center gap-2.5 text-sm",
        props.disabled && "cursor-not-allowed opacity-50",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs("span", { className: "relative inline-flex", children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              ref,
              id: inputId,
              type: "checkbox",
              className: "peer size-[1.15rem] shrink-0 cursor-pointer appearance-none rounded border border-input bg-background transition-colors duration-fast ease-out-expo checked:border-primary checked:bg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
              ...props
            }
          ),
          /* @__PURE__ */ jsx(
            Check,
            {
              className: "pointer-events-none absolute left-1/2 top-1/2 size-3.5 -translate-x-1/2 -translate-y-1/2 scale-50 text-primary-foreground opacity-0 transition-[opacity,transform] duration-base ease-spring peer-checked:scale-100 peer-checked:opacity-100",
              "aria-hidden": "true",
              strokeWidth: 3
            }
          )
        ] }),
        label && /* @__PURE__ */ jsx("span", { className: "flex-1 text-foreground", children: label }),
        hint && /* @__PURE__ */ jsx("span", { className: "text-xs tabular-nums text-muted-foreground", children: hint })
      ]
    }
  );
});
function MultiSelect({
  label,
  placeholder = "All",
  options,
  value,
  onToggle,
  className,
  "aria-label": ariaLabel,
  placement = "auto"
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [dropUp, setDropUp] = useState(false);
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const labelId = useId();
  useEffect(() => {
    if (!open) return;
    if (placement === "top") {
      setDropUp(true);
    } else if (placement === "bottom") {
      setDropUp(false);
    } else if (rootRef.current) {
      const rect = rootRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setDropUp(spaceBelow < 340);
    }
  }, [open, placement]);
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    function onKeyDown(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);
  const searchable = options.length > 10;
  useEffect(() => {
    if (open && searchable) searchRef.current?.focus();
    else if (!open) setQuery("");
  }, [open, searchable]);
  const filteredOptions = useMemo(() => {
    if (!searchable) return options;
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, query, searchable]);
  const summary = value.length === 0 ? placeholder : value.length === 1 ? options.find((o) => o.value === value[0])?.label ?? placeholder : `${value.length} selected`;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-1.5", className), ref: rootRef, children: [
    label && /* @__PURE__ */ jsx("span", { id: labelId, className: "text-sm font-medium text-foreground", children: label }),
    /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          "aria-haspopup": "listbox",
          "aria-expanded": open,
          "aria-labelledby": label ? labelId : void 0,
          "aria-label": label ? void 0 : ariaLabel,
          onClick: () => setOpen((o) => !o),
          className: cn(
            "flex h-11 w-full items-center justify-between rounded border border-input bg-background pl-3.5 pr-3 text-sm text-foreground shadow-sm transition-colors duration-fast ease-out-expo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
          ),
          children: [
            /* @__PURE__ */ jsx("span", { className: cn("truncate", value.length === 0 && "text-muted-foreground"), children: summary }),
            /* @__PURE__ */ jsx(
              ChevronDown,
              {
                className: cn(
                  "size-4 shrink-0 text-muted-foreground transition-transform duration-fast ease-out-expo",
                  open && "rotate-180"
                ),
                "aria-hidden": "true"
              }
            )
          ]
        }
      ),
      open && /* @__PURE__ */ jsxs(
        "div",
        {
          role: "listbox",
          "aria-multiselectable": "true",
          className: cn(
            "absolute z-30 w-full rounded border border-border bg-card p-2 shadow-lg animate-in fade-in-0 zoom-in-95 duration-fast ease-out-expo",
            dropUp ? "bottom-full mb-1.5" : "top-full mt-1.5"
          ),
          children: [
            searchable && /* @__PURE__ */ jsxs("div", { className: "relative mb-2", children: [
              /* @__PURE__ */ jsx(
                Search,
                {
                  className: "pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground",
                  "aria-hidden": "true"
                }
              ),
              /* @__PURE__ */ jsx(
                "input",
                {
                  ref: searchRef,
                  type: "text",
                  value: query,
                  onChange: (e) => setQuery(e.target.value),
                  placeholder: "Search...",
                  "aria-label": `Search ${ariaLabel ?? label ?? "options"}`,
                  className: "h-9 w-full rounded border border-input bg-background pl-8 pr-3 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
                }
              )
            ] }),
            /* @__PURE__ */ jsx("div", { className: "max-h-56 overflow-y-auto", children: filteredOptions.length === 0 ? /* @__PURE__ */ jsx("p", { className: "px-2 py-1.5 text-sm text-muted-foreground", children: options.length === 0 ? "No options" : "No matches" }) : /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-0.5", children: filteredOptions.map((opt) => /* @__PURE__ */ jsx(
              Checkbox,
              {
                label: opt.label,
                hint: opt.hint,
                checked: value.includes(opt.value),
                onChange: () => onToggle(opt.value),
                className: "rounded px-1.5 py-1.5 hover:bg-muted"
              },
              opt.value
            )) }) })
          ]
        }
      )
    ] })
  ] });
}
function Modal({
  open,
  onOpenChange,
  title,
  hideTitle = false,
  description,
  children,
  footer,
  className
}) {
  return /* @__PURE__ */ jsx(Dialog.Root, { open, onOpenChange, children: /* @__PURE__ */ jsxs(Dialog.Portal, { children: [
    /* @__PURE__ */ jsx(
      Dialog.Overlay,
      {
        className: "fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
        onClick: (e) => e.stopPropagation()
      }
    ),
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4",
        onClick: (e) => e.stopPropagation(),
        children: /* @__PURE__ */ jsxs(
          Dialog.Content,
          {
            onClick: (e) => e.stopPropagation(),
            className: cn(
              "w-full max-w-lg",
              "glass-strong rounded-xl p-6 text-card-foreground",
              "focus:outline-none data-[state=open]:animate-fade-up data-[state=closed]:animate-fade-down",
              className
            ),
            children: [
              /* @__PURE__ */ jsxs("div", { className: "mb-4 flex items-start justify-between gap-4", children: [
                /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
                  /* @__PURE__ */ jsx(Dialog.Title, { className: cn("text-lg font-semibold", hideTitle && "sr-only"), children: title }),
                  description && /* @__PURE__ */ jsx(Dialog.Description, { className: "text-sm text-muted-foreground", children: description })
                ] }),
                /* @__PURE__ */ jsx(
                  Dialog.Close,
                  {
                    className: "rounded p-1 text-muted-foreground transition-colors duration-fast ease-out-expo hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    "aria-label": "Close dialog",
                    children: /* @__PURE__ */ jsx(X, { className: "size-5", "aria-hidden": "true" })
                  }
                )
              ] }),
              /* @__PURE__ */ jsx("div", { children }),
              footer && /* @__PURE__ */ jsx("div", { className: "mt-6 flex justify-end gap-3", children: footer })
            ]
          }
        )
      }
    )
  ] }) });
}
function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  side = "bottom",
  className
}) {
  const sideClasses = side === "bottom" ? "inset-x-0 bottom-0 max-h-[85vh] rounded-t-xl data-[state=open]:animate-slide-in-bottom data-[state=closed]:animate-slide-out-bottom" : "inset-y-0 right-0 h-full w-[min(24rem,90vw)] data-[state=open]:animate-slide-in-right data-[state=closed]:animate-slide-out-right";
  return /* @__PURE__ */ jsx(Dialog.Root, { open, onOpenChange, children: /* @__PURE__ */ jsxs(Dialog.Portal, { children: [
    /* @__PURE__ */ jsx(Dialog.Overlay, { className: "fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out" }),
    /* @__PURE__ */ jsxs(
      Dialog.Content,
      {
        className: cn(
          "glass-strong fixed z-50 flex flex-col text-card-foreground focus:outline-none",
          sideClasses,
          className
        ),
        children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-4 border-b border-border p-4", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
              /* @__PURE__ */ jsx(Dialog.Title, { className: "text-base font-semibold", children: title }),
              description && /* @__PURE__ */ jsx(Dialog.Description, { className: "text-sm text-muted-foreground", children: description })
            ] }),
            /* @__PURE__ */ jsx(
              Dialog.Close,
              {
                className: "rounded p-1 text-muted-foreground transition-colors duration-fast ease-out-expo hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                "aria-label": "Close",
                children: /* @__PURE__ */ jsx(X, { className: "size-5", "aria-hidden": "true" })
              }
            )
          ] }),
          /* @__PURE__ */ jsx("div", { className: "flex-1 overflow-y-auto p-4", children }),
          footer && /* @__PURE__ */ jsx("div", { className: "flex items-center gap-3 border-t border-border p-4", children: footer })
        ]
      }
    )
  ] }) });
}

export { Badge, Button, Card, Checkbox, Container, Input, Modal, MultiSelect, SearchableSelect, Section, Select, Sheet, Skeleton, buttonVariants, cn };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map